import { parse } from 'acorn'
import { simple } from 'acorn-walk'
import { generate } from 'astring'
import type * as ES from 'estree'

/** A parse or structure error in plot code, with a 1-based line and 0-based column. */
export class PlotSyntaxError extends Error {
  readonly line: number
  readonly column: number
  constructor(message: string, line: number, column: number) {
    super(message)
    this.name = 'PlotSyntaxError'
    this.line = line
    this.column = column
  }
}

/** Identifiers a plot may not use: eval escapes the shadowed globals, __tick is ours. */
const FORBIDDEN_NAMES = new Set(['eval', '__tick'])

type Loc = { start: { line: number; column: number } } | null | undefined

const fail = (message: string, loc: Loc): never => {
  throw new PlotSyntaxError(message, loc?.start.line ?? 1, loc?.start.column ?? 0)
}

const tickStatement = (): ES.ExpressionStatement => ({
  type: 'ExpressionStatement',
  expression: {
    type: 'CallExpression',
    callee: { type: 'Identifier', name: '__tick' },
    arguments: [],
    optional: false,
  },
})

const withTick = (body: ES.Statement): ES.BlockStatement => ({
  type: 'BlockStatement',
  body: body.type === 'BlockStatement' ? [tickStatement(), ...body.body] : [tickStatement(), body],
})

const parseModule = (code: string): ES.Program => {
  try {
    return parse(code, {
      ecmaVersion: 2022,
      sourceType: 'module',
      locations: true,
    }) as unknown as ES.Program
  } catch (err) {
    const e = err as { message?: string; loc?: { line: number; column: number } }
    const message = (e.message ?? String(err)).replace(/\s*\(\d+:\d+\)$/, '')
    return fail(message, e.loc ? { start: e.loc } : undefined)
  }
}

/** Turns `export function draw` into `function draw`; rejects everything module-ish beyond that. */
const unwrapExports = (program: ES.Program): ES.Program => ({
  ...program,
  body: program.body.map((node): ES.Statement | ES.ModuleDeclaration => {
    if (node.type === 'ExportNamedDeclaration' && node.declaration) return node.declaration
    if (node.type === 'ExportNamedDeclaration')
      return fail('export lists are not allowed; use export function draw', node.loc)
    if (node.type === 'ExportDefaultDeclaration')
      return fail('export default is not allowed; use export function draw', node.loc)
    if (node.type === 'ImportDeclaration') return fail('plots cannot import anything', node.loc)
    if (node.type === 'ExportAllDeclaration') return fail('export * is not allowed', node.loc)
    return node
  }),
})

const guardFunction = (node: ES.Function): void => {
  const fn = node as ES.Function & { expression?: boolean }
  if (fn.body.type === 'BlockStatement') {
    fn.body = withTick(fn.body)
    return
  }
  const returned: ES.ReturnStatement = { type: 'ReturnStatement', argument: fn.body }
  fn.body = { type: 'BlockStatement', body: [tickStatement(), returned] }
  fn.expression = false
}

const guardLoop = (node: ES.Statement & { body: ES.Statement }): void => {
  node.body = withTick(node.body)
}

const checkIdentifier = (node: ES.Identifier): void => {
  if (FORBIDDEN_NAMES.has(node.name)) fail(`${node.name} is not allowed in a plot`, node.loc)
}

/**
 * Rewrites plot source so every function entry and loop iteration calls
 * `__tick()`, which the runtime uses to enforce the per-frame budget.
 */
export const instrument = (code: string): string => {
  const program = unwrapExports(parseModule(code))
  simple(program as never, {
    Function: (n) => guardFunction(n as unknown as ES.Function),
    ForStatement: (n) => guardLoop(n as unknown as ES.ForStatement),
    ForInStatement: (n) => guardLoop(n as unknown as ES.ForInStatement),
    ForOfStatement: (n) => guardLoop(n as unknown as ES.ForOfStatement),
    WhileStatement: (n) => guardLoop(n as unknown as ES.WhileStatement),
    DoWhileStatement: (n) => guardLoop(n as unknown as ES.DoWhileStatement),
    Identifier: (n) => checkIdentifier(n as unknown as ES.Identifier),
  })
  return generate(program)
}
